import { useState, useEffect } from 'react';
import { useUpdateLead, Lead } from '@/hooks/useLeads';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';

interface EditLeadModalProps {
  lead: Lead | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SOURCES = ['manual', 'meta', 'google', 'linkedin', 'website', 'whatsapp', 'other'] as const;
const TEMPERATURES = ['hot', 'warm', 'cold'] as const;
const STAGES = ['new', 'contacted', 'appointment', 'proposal', 'won', 'lost'] as const;

export function EditLeadModal({ lead, open, onOpenChange }: EditLeadModalProps) {
  const updateLead = useUpdateLead();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    source: 'manual' as string,
    temperature: 'hot' as string, // Default to HOT for new leads
    pipeline_stage: 'new' as string,
    notes_short: '',
    notes_long: '',
  });

  useEffect(() => {
    if (lead) {
      setFormData({
        name: lead.name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        source: lead.source || 'manual',
        temperature: lead.temperature || 'hot', // Default to HOT if missing
        pipeline_stage: lead.pipeline_stage || 'new',
        notes_short: lead.notes_short || '',
        notes_long: lead.notes_long || '',
      });
    }
  }, [lead]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lead || !formData.name.trim()) return;

    await updateLead.mutateAsync({
      id: lead.id,
      updates: {
        name: formData.name.trim(),
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        source: formData.source as Lead['source'],
        temperature: formData.temperature as Lead['temperature'],
        pipeline_stage: formData.pipeline_stage as Lead['pipeline_stage'],
        notes_short: formData.notes_short.trim() || null,
        notes_long: formData.notes_long.trim() || null,
      },
    });

    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Lead</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="edit-name">Name *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Full name"
                required
              />
            </div>
            
            <div>
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@example.com"
              />
            </div>
            
            <div>
              <Label htmlFor="edit-phone">Phone</Label>
              <Input
                id="edit-phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+1 555 123 4567"
              />
            </div>

            <div>
              <Label htmlFor="edit-source">Source</Label>
              <Select value={formData.source} onValueChange={(v) => setFormData({ ...formData, source: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOURCES.map((s) => (
                    <SelectItem key={s} value={s} className="capitalize">
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit-temperature">Temperature</Label>
              <Select value={formData.temperature} onValueChange={(v) => setFormData({ ...formData, temperature: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TEMPERATURES.map((t) => (
                    <SelectItem key={t} value={t} className="capitalize">
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="edit-stage">Stage</Label>
              <Select value={formData.pipeline_stage} onValueChange={(v) => setFormData({ ...formData, pipeline_stage: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.map((s) => (
                    <SelectItem key={s} value={s} className="capitalize">
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2">
              <Label htmlFor="edit-notes-short">Short Notes</Label>
              <Textarea
                id="edit-notes-short"
                value={formData.notes_short}
                onChange={(e) => setFormData({ ...formData, notes_short: e.target.value })}
                placeholder="Quick notes..."
                rows={2}
              />
            </div>

            <div className="col-span-2">
              <Label htmlFor="edit-notes-long">Detailed Notes</Label>
              <Textarea
                id="edit-notes-long"
                value={formData.notes_long}
                onChange={(e) => setFormData({ ...formData, notes_long: e.target.value })}
                placeholder="Detailed notes about interactions, requirements, etc..."
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateLead.isPending || !formData.name.trim()}>
              {updateLead.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
